import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, X, ClipboardCheck, Link2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const STAGES = ['Pre-Inspection', 'WIP', 'Final'];
const ROLES = ['Client', 'Consultant', 'Internal'];

const Label = ({ children }) => (
  <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">{children}</label>
);

const TYPE_BADGE = { H: 'text-rose-600 bg-rose-50 border-rose-200', W: 'text-amber-700 bg-amber-50 border-amber-200', S: 'text-blue-700 bg-blue-50 border-blue-200', R: 'text-slate-600 bg-slate-100 border-slate-200' };

export default function RaiseInspection() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    project_id: '', stage: 'WIP', inspector_role: 'Internal', activity: '',
    requested_inspector: '', scheduled_at: '', from_template_id: '',
  });
  const [items, setItems] = useState([]);
  const [itemInput, setItemInput] = useState('');
  const [saving, setSaving] = useState(false);
  // ITP link state
  const [itps, setItps] = useState([]);
  const [selectedItp, setSelectedItp] = useState(null);
  const [itpItemSeq, setItpItemSeq] = useState('');

  useEffect(() => {
    apiGet('/projects').then((p) => { setProjects(p); if (p.length) setForm((f) => ({ ...f, project_id: p[0].id })); }).catch(() => toast.error('Failed to load projects'));
    apiGet('/quality/inspection-templates').then((d) => setTemplates(d.templates || [])).catch(() => {});
  }, []);

  // Fetch ITPs when project changes
  useEffect(() => {
    if (!form.project_id) return;
    setSelectedItp(null); setItpItemSeq(''); setItps([]);
    apiGet('/quality/itps', { project_id: form.project_id })
      .then((d) => setItps((d.items || []).filter((it) => ['Approved', 'Submitted'].includes(it.status))))
      .catch(() => {});
  }, [form.project_id]);

  const selectItp = (itpId) => {
    const itp = itps.find((i) => i.id === itpId) || null;
    setSelectedItp(itp);
    setItpItemSeq('');
    // Auto-fill activity from first item if form activity is empty
    if (itp && !form.activity && itp.items?.length) {
      setForm((f) => ({ ...f, activity: itp.items[0].activity || f.activity }));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickTemplate = (e) => {
    const id = e.target.value;
    setForm({ ...form, from_template_id: id });
    const t = templates.find((x) => x.id === id);
    setItems(t ? [...t.items] : []);
  };

  const addItem = () => {
    const v = itemInput.trim();
    if (v && !items.includes(v)) setItems([...items, v]);
    setItemInput('');
  };

  const submit = async () => {
    if (!form.project_id) { toast.error('Select a project'); return; }
    if (!form.activity.trim()) { toast.error('Activity is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        checklist_items: items.map((it) => ({ item: it })),
        itp_id: selectedItp?.id || undefined,
        itp_item_seq: itpItemSeq ? parseInt(itpItemSeq, 10) : undefined,
      };
      const doc = await apiPost('/quality/inspection-requests', payload);
      toast.success('Inspection request raised');
      navigate(`/app/quality/inspection-requests/${doc.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-0 py-4 sm:py-0">
      <PageHeader overline="Quality" title="Raise Inspection Request" subtitle="Request a stage inspection with a checklist (Client / Consultant / Internal)" />
      <div className="space-y-4 bg-white sm:border sm:border-slate-200 rounded-sm sm:p-6" data-testid="raise-inspection-form">
        <div>
          <Label>Project</Label>
          <select value={form.project_id} onChange={set('project_id')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white" data-testid="insp-project-select">
            {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Stage</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {STAGES.map((s) => (
                <button key={s} type="button" onClick={() => setForm({ ...form, stage: s })}
                  className={`px-1 py-2 rounded-sm text-[10px] font-bold border transition-colors ${form.stage === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'}`}
                  data-testid={`insp-stage-${s.replace(/\s/g, '-').toLowerCase()}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Inspector</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {ROLES.map((r) => (
                <button key={r} type="button" onClick={() => setForm({ ...form, inspector_role: r })}
                  className={`px-1 py-2 rounded-sm text-[10px] font-bold border transition-colors ${form.inspector_role === r ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'}`}
                  data-testid={`insp-role-${r.toLowerCase()}`}>{r}</button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <Label>Activity / what's being inspected</Label>
          <input value={form.activity} onChange={set('activity')} placeholder="e.g. RCC slab L4 pre-pour" className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="insp-activity" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Requested inspector (name)</Label>
            <input value={form.requested_inspector} onChange={set('requested_inspector')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="insp-inspector" />
          </div>
          <div>
            <Label>Scheduled at</Label>
            <input type="datetime-local" value={form.scheduled_at} onChange={set('scheduled_at')} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm" data-testid="insp-scheduled" />
          </div>
        </div>

        {/* ITP Link (optional) */}
        <div className="border border-dashed border-slate-300 rounded-sm p-3 space-y-2 bg-slate-50">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Link2 size={11} /> Link to ITP Item <span className="font-normal lowercase tracking-normal text-slate-400">— optional, for traceability</span>
          </div>
          <select value={selectedItp?.id || ''} onChange={(e) => selectItp(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
            data-testid="insp-itp-select">
            <option value="">— No ITP link (ad-hoc inspection) —</option>
            {itps.map((i) => <option key={i.id} value={i.id}>[{i.discipline}] {i.title} · {i.revision}</option>)}
          </select>
          {selectedItp && (
            <select value={itpItemSeq} onChange={(e) => setItpItemSeq(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
              data-testid="insp-itp-item-select">
              <option value="">— Select ITP inspection point —</option>
              {(selectedItp.items || []).map((it) => (
                <option key={it.seq} value={it.seq}>
                  [{it.inspection_type}] {it.activity}
                  {it.sign_off_status === 'signed' ? ' ✓' : ''}
                </option>
              ))}
            </select>
          )}
          {selectedItp && itpItemSeq && (() => {
            const item = (selectedItp.items || []).find((it) => it.seq === parseInt(itpItemSeq, 10));
            if (!item) return null;
            return (
              <div className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold border rounded-sm ${TYPE_BADGE[item.inspection_type] || ''}`}>
                {item.inspection_type_label || item.inspection_type} — {item.activity}
              </div>
            );
          })()}
        </div>

        <div>
          <Label>Checklist {templates.length > 0 && '— pick a template or add items'}</Label>
          {templates.length > 0 && (
            <select value={form.from_template_id} onChange={pickTemplate} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white mb-2" data-testid="insp-template-select">
              <option value="">— No template (build manually) —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <div className="flex gap-2 mb-2">
            <input value={itemInput} onChange={(e) => setItemInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }} placeholder="Add a checklist item" className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="insp-item-input" />
            <button type="button" onClick={addItem} className="px-3 py-2 bg-slate-100 rounded-sm hover:bg-slate-200" data-testid="insp-item-add"><Plus size={16} /></button>
          </div>
          <div className="space-y-1">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-slate-50 px-3 py-1.5 rounded-sm">
                <span>{it}</span>
                <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))}><X size={13} className="text-slate-400" /></button>
              </div>
            ))}
          </div>
        </div>
        <button onClick={submit} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-[#3b4872] hover:bg-[#2d3856] text-white px-6 py-3.5 rounded-sm disabled:opacity-60 transition-colors font-display font-medium" data-testid="insp-submit-btn">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <ClipboardCheck size={16} />}
          {saving ? 'Submitting…' : 'Raise Inspection Request'}
        </button>
      </div>
    </div>
  );
}
